import { forwardRef, useImperativeHandle, useMemo, useState } from 'react'
import Link from '@/components/atoms/link'
import { NavigationItem } from '@/network/graphql.g'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import { HomeBanner } from '@/network/graphql.g'
import Image from 'next/image'
import { applyImagePolicy } from '@/utils/imageExtension'
import { MobileSubcategoriesMenuRedesignActions } from '@/components/organisms/mobileSubcategoriesMenuRedesign'
import {
  campaignNavigationLinks,
  Gender,
  genderCategoryMap,
  GenderEnum
} from '@/components/organisms/toolbar/desktop/campaignNavigationLinks'

type MobileSubcategoriesMenuRedesignProps = {
  categoryUrl: string
  navigationItems: NavigationItem[]
  banners: HomeBanner[]
  currentCategoryId: string
}

const MobileSubcategoryItemsMenuRedesign = forwardRef<
  MobileSubcategoriesMenuRedesignActions,
  MobileSubcategoriesMenuRedesignProps
>((props, ref) => {
  const { t } = useTranslation('common')
  const [subcategoriesInfo, setSubcategoriesInfo] = useState<{
    categoryUrl: string
    subcategories: NavigationItem[]
  }>({
    categoryUrl: props.categoryUrl,
    subcategories: props.navigationItems
  })

  useImperativeHandle(ref, () => ({
    setSubcategories: (categoryUrl, subcategories) => {
      setSubcategoriesInfo({
        categoryUrl,
        subcategories
      })
    }
  }))
  const { subcategories } = subcategoriesInfo

  const gender = useMemo<Gender | null>(() => {
    const id = props.currentCategoryId

    switch (true) {
      case !id:
        return null
      case genderCategoryMap[GenderEnum.Women].includes(id):
        return GenderEnum.Women
      case genderCategoryMap[GenderEnum.Men].includes(id):
        return GenderEnum.Men
      case genderCategoryMap[GenderEnum.Children].includes(id):
        return GenderEnum.Children
      default:
        return null
    }
  }, [props.currentCategoryId])

  const filteredCampaigns = useMemo(() => {
    if (!gender) {
      return []
    }
    return campaignNavigationLinks.filter((c) => c.gender === gender)
  }, [gender])

  return (
    <>
      <ItemsList ref={ref} data-testid="mobileItemsList">
        {subcategories.length > 0 &&
          [
            {
              categoryId: '-1',
              name: t('Category.showAll'),
              url: `/${props.categoryUrl}`
            } as NavigationItem,
            ...subcategories
          ].map((subcategory) => {
            const href = `/${subcategory?.url.replace(/^\/+|\/+$/g, '')}`
            return (
              <SubcategoryCard
                key={subcategory.categoryId}
                data-testid={subcategory.categoryId}
              >
                <StyledLink href={href}>
                  <SubcategoryName>{subcategory?.name}</SubcategoryName>
                  {subcategory.categoryId !== '-1' && (
                    <ImageWrapper>
                      <Image
                        src={applyImagePolicy(
                          `/static/mob-cat/mob-cat-${subcategory?.categoryId}.webp`
                        )}
                        width={34}
                        height={50}
                        unoptimized
                        objectFit="cover"
                      />
                    </ImageWrapper>
                  )}
                </StyledLink>
              </SubcategoryCard>
            )
          })}
      </ItemsList>
      <ItemsList>
        <SubcategoryTitle>{t('Campaign.title')}</SubcategoryTitle>
        {filteredCampaigns.map((campaign) => {
          const href = `/${campaign.url.replace(/^\/+|\/+$/g, '')}`
          return (
            <SubcategoryCard key={campaign.campaignId}>
              <StyledLink href={href}>
                <SubcategoryName>{t(campaign.nameKey)}</SubcategoryName>
              </StyledLink>
            </SubcategoryCard>
          )
        })}
      </ItemsList>
    </>
  )
})

export default MobileSubcategoryItemsMenuRedesign

const ItemsList = styled.ul`
  margin: 0 0 10px;
  padding: 0 0 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SubcategoryCard = styled.li`
  background: ${(props) => props.theme.colors.navMenuGrey};
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s;
  min-height: 58px;
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 16px;
  width: 100%;
`

const SubcategoryName = styled.h2`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.onBackground};
  margin: 0;
  text-transform: ${(props) => props.isBigButton && 'uppercase'};
`

const ImageWrapper = styled.div`
  width: 60px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;
`

const SubcategoryTitle = styled.h2`
  padding-bottom: 5px;
  margin: 0;
  display: block;
  font-size: 14px;
  font-weight: 500;
  position: relative;
  text-align: center;
  color: ${(props) => props.theme.colors.towerColorSecondary};
`
